import { Colors, Icon, useModal } from "swing-components";

import { PUT, translate, useIsDesktop } from "~utils";

import { GeocodeData, LocationFilterValueTypes } from "../FilterBar/types";
import { RadiusActionSheet } from "../RadiusActionSheet";
import { ZipcodeActionSheet } from "../ZipcodeActionSheet";

type LocationFilterBarProps = {
  values: LocationFilterValueTypes;
  onChange: (values: LocationFilterValueTypes) => void;
};

const linkButtonStyle = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  color: Colors.blue500,
  background: "none",
  border: "none",
  padding: 0,
};

export function LocationFilterBar({ values, onChange }: LocationFilterBarProps) {
  const isDesktop = useIsDesktop();

  // Update user preferences on change
  const updateUserPreferences = async (zipcode: string, radius: number) => {
    try {
      await PUT("/api/sub/preferences", {
        zipcode,
        radius,
      });
    } catch (error) {
      console.warn("Failed to update user preferences:", error);
    }
  };

  const handleZipcodeApply = (newZipcode: string, geocode: GeocodeData) => {
    const recentZipcodes = values.recentZipcodes.includes(newZipcode)
      ? values.recentZipcodes
      : [newZipcode, ...values.recentZipcodes].slice(0, 5);

    const newValues = { ...values, zipcode: newZipcode, geocode, recentZipcodes };
    onChange(newValues);
    updateUserPreferences(newZipcode, newValues.radius);
  };

  const handleRadiusApply = (newValues: LocationFilterValueTypes) => {
    onChange(newValues);
    if (newValues.zipcode) {
      updateUserPreferences(newValues.zipcode, newValues.radius);
    }
  };

  const modalOptions = {
    cssClass: isDesktop ? undefined : "action-sheet-modal",
    breakpoints: isDesktop ? undefined : [0, 1],
    initialBreakpoint: isDesktop ? undefined : 1,
  };

  const { openModal: openRadius, closeModal: closeRadius } = useModal({
    component: (
      <RadiusActionSheet
        onDismiss={() => closeRadius()}
        onApply={handleRadiusApply}
        currentValues={values}
      />
    ),
    modalOptions,
  });

  const { openModal: openZip, closeModal: closeZip } = useModal({
    component: (
      <ZipcodeActionSheet
        onDismiss={() => closeZip()}
        onApply={handleZipcodeApply}
        currentZipcode={values.zipcode}
        recentZipcodes={values.recentZipcodes}
        userZipcode={values.userZipcode}
        userState={values.userState}
      />
    ),
    modalOptions,
  });

  if (!values.zipcode) return null;

  return (
    <div css={{ display: "flex", flexDirection: "row", gap: 4 }}>
      {translate("LOCATION_FILTER_SHOWING_JOBS")}
      <button css={linkButtonStyle} onClick={() => openRadius()}>
        <span css={{ textDecoration: "underline", fontWeight: 600 }}>
          {values.radius > 60 ? "61+" : values.radius} mi
        </span>
        <Icon name="Chevron Down" color={Colors.blue500} />
      </button>
      {translate("LOCATION_FILTER_OF")}
      <button css={linkButtonStyle} onClick={() => openZip()}>
        <span css={{ textDecoration: "underline", fontWeight: 600 }}>{values.zipcode}</span>
        <Icon name="Chevron Down" color={Colors.blue500} />
      </button>
    </div>
  );
}
